import { useEffect, useRef, type MutableRefObject, type RefObject } from "react";
import type {
  PlayerPlaybackFailure,
  PlayerVideoHandle,
} from "../components/playerVideoTypes";
import type { PlaybackLifecycleEvent } from "./lifecycle";
import {
  usePlayerMediaRuntime,
  type PlayerMediaRuntimeInput,
} from "./usePlayerMediaRuntime";
import { usePlayerTransportRuntime } from "./usePlayerTransportRuntime";
import { planDecoderLoad } from "./decoderEvents";
import {
  planAdjustVolume,
  planPlaybackFailure,
  planPlaybackRetry,
  planSeekIntent,
  planSetVolume,
  planSourceSelection,
  planSuspendPlayback,
  planToggleMuted,
  planTogglePaused,
  type DecoderEventDecision,
} from "./decoderEvents";
import { executePlayerDecision } from "./commandExecutor";
import { createDecoderEventHandlers } from "./decoderCoordinator";
import { coordinatePlaybackFailure } from "./failureCoordinator";
import { isManagedHlsSource } from "./streamPlan";
import { decidePlaybackGenerationFailure } from "./hlsRecovery";
import type { MediaSource } from "./presentation";
import type { usePlayerController } from "./usePlayerController";

type PlayerSourcePort = ReturnType<typeof usePlayerController>["sourcePort"];

type PlayerRuntimeAdaptersInput = PlayerMediaRuntimeInput & {
  lifecycleDuration: number;
  lifecycleBuffered: number;
  lifecycleVolume: number;
  positionRef: MutableRefObject<number>;
  pausedRef: MutableRefObject<boolean>;
  mutedRef: MutableRefObject<boolean>;
  startedRef: MutableRefObject<boolean>;
  videoRef: RefObject<PlayerVideoHandle>;
  dispatchLifecycle: (event: PlaybackLifecycleEvent) => void;
  markProgressReported: (seconds: number) => void;
  onSeekTimeout: () => void;
  onActivity: () => void;
  bindSurface: (uri?: string) => string;
  sourcePort: PlayerSourcePort;
  sourceRecovering: boolean;
  surfaceEpoch: number;
  setServerSeek: (intent: { itemId: string; seconds: number } | null) => void;
  shouldReportProgress: (seconds: number, intervalSeconds?: number) => boolean;
  setRecoveryNotice: (message: string) => void;
  showControls: () => void;
  showPlaybackPulse: (kind: "play" | "pause") => void;
  closeMenu: () => void;
  playItem: (itemId: string) => void;
  nextEpisodePromptEnabled: boolean;
  /** Enables non-blocking metadata queries after the active decoder is ready. */
  decoderReady: boolean;
};

/** Final composition boundary for media and transport runtime adapters. */
export function usePlayerRuntimeAdapters(input: PlayerRuntimeAdaptersInput) {
  const sameSourceRecoveryRef = useRef(new Set<string>());
  const playedSourceRef = useRef(new Set<string>());
  const failedSourceRef = useRef(new Set<string>());
  const media = usePlayerMediaRuntime(input);
  const timelineDuration = Math.max(
    input.lifecycleDuration,
    (media.source?.RunTimeTicks ?? media.item?.RunTimeTicks ?? 0) / 10_000_000,
    1,
  );
  const transport = usePlayerTransportRuntime({
    credentials: input.credentials,
    itemId: input.itemId,
    mediaSourceId: media.source?.Id,
    playSessionId: media.playbackQuery.data?.PlaySessionId,
    playMethod: media.playMethod,
    timelineDuration,
    decoderOffsetSeconds: media.streamStartSeconds,
    positionRef: input.positionRef,
    pausedRef: input.pausedRef,
    mutedRef: input.mutedRef,
    startedRef: input.startedRef,
    videoRef: input.videoRef,
    dispatchLifecycle: input.dispatchLifecycle,
    markProgressReported: input.markProgressReported,
    onSeekTimeout: input.onSeekTimeout,
    onActivity: input.onActivity,
  });
  const surfaceIdentity = input.bindSurface(media.streamUrl);
  useEffect(() => {
    if (!media.streamUrl) return;
    const decision = planDecoderLoad(input.sourceRecovering);
    for (const step of decision.steps) {
      if (step.type === "LIFECYCLE") input.dispatchLifecycle(step.event);
    }
  }, [media.streamUrl, input.surfaceEpoch]);

  const seek = (requestedSeconds: number, reportIntent = true) => {
    applyDecision(planSeekIntent({
      requestedSeconds,
      timelineDuration,
      managedHls: media.managedHls,
      streamStartSeconds: media.streamStartSeconds,
      decoderDurationSeconds: input.lifecycleDuration,
      decoderBufferedSeconds: input.lifecycleBuffered,
      itemId: input.itemId,
      reportIntent,
    }));
  };

  const applyDecision = (decision: DecoderEventDecision) => {
    executePlayerDecision(decision, {
      dispatchLifecycle: input.dispatchLifecycle,
      clearRecoveryNotice: () => input.setRecoveryNotice(""),
      setRecoveryNotice: input.setRecoveryNotice,
      markSourcePlaying: () => {
        const healthKey = `${input.itemId}:${media.source?.Id ?? "unknown"}`;
        if (!playedSourceRef.current.has(healthKey)) {
          media.recordSourceOutcome(media.source, "success");
        }
        playedSourceRef.current.add(healthKey);
        sameSourceRecoveryRef.current.clear();
        input.sourcePort.markPlaying();
      },
      sessionStart: transport.session.start,
      sessionProgress: transport.session.progress,
      sessionStop: transport.session.stop,
      seek,
      seekDecoder: transport.seek.seek,
      clearSeek: transport.seek.clear,
      showControls: input.showControls,
      playNext: () => {
        if (media.nextEpisode?.Id) input.playItem(media.nextEpisode.Id);
      },
      setServerSeek: (itemId, seconds) => input.setServerSeek(
        itemId && seconds !== undefined ? { itemId, seconds } : null,
      ),
      beginSource: ({ sourceId, ...options }) =>
        input.sourcePort.beginSource(sourceId, options),
      retrySource: input.sourcePort.retry,
      resetTracks: media.resetTracks,
      closeMenu: input.closeMenu,
      refetchPlaybackInfo: () => { void media.playbackQuery.refetch(); },
      showPlaybackPulse: input.showPlaybackPulse,
    });
  };

  const selectSource = (next: MediaSource, recovering = false) => {
    applyDecision(planSourceSelection({
      itemId: input.itemId,
      sourceId: next.Id ?? "",
      managedHls: isManagedHlsSource(next),
      recovering,
      positionSeconds: input.positionRef.current,
      paused: input.pausedRef.current,
      sessionStarted: input.startedRef.current,
    }));
  };

  const handlePlaybackError = (
    message: string,
    failure?: PlayerPlaybackFailure,
  ) => {
    if (!input.sourcePort.isActiveSurface(surfaceIdentity)) return;
    const recoveryKey = `${input.itemId}:${media.source?.Id ?? "unknown"}`;
    if (!failedSourceRef.current.has(recoveryKey)) {
      failedSourceRef.current.add(recoveryKey);
      media.recordSourceOutcome(media.source, "failure");
    }
    const sourceHasPlayed =
      input.startedRef.current ||
      playedSourceRef.current.has(recoveryKey);
    const failureAction = decidePlaybackGenerationFailure({
      retryableGenerationFailure: Boolean(
        failure?.fatal &&
        (failure.kind === "session-gone" || failure.kind === "network"),
      ),
      sameSourceRecoveryUsed: sameSourceRecoveryRef.current.has(recoveryKey),
      sourceHasPlayed,
      automaticSourceRecoveryEnabled:
        input.preferences.simplePlaybackRecovery || input.preferences.playbackQuality === "adaptive",
    });
    // A cold managed-HLS source has already consumed Remux's complete
    // readiness window before its fatal 410/network failure reaches us.
    // Rebuilding that same unplayed generation merely pays the startup cost
    // twice. Preserve one same-source regeneration for streams that had
    // actually played; cold failures move directly to the next ranked source.
    if (failureAction === "regenerate-source") {
      sameSourceRecoveryRef.current.add(recoveryKey);
      applyDecision(planPlaybackRetry({
        positionSeconds: input.positionRef.current,
        paused: input.pausedRef.current,
        sessionStarted: input.startedRef.current,
      }));
      return;
    }
    if (failureAction === "fail-for-user") {
      applyDecision(planPlaybackFailure({
        stale: false,
        message,
        sessionStarted: input.startedRef.current,
      }));
      return;
    }
    coordinatePlaybackFailure({
      message,
      itemId: input.itemId,
      currentSourceId: media.source?.Id,
      rankedSources: media.rankedSources,
      // Failed startup must always escape the current source. The preference
      // controls automatic switching only after a source has actually played.
      recoveryEnabled:
        input.preferences.simplePlaybackRecovery || input.preferences.playbackQuality === "adaptive" || !sourceHasPlayed,
      positionSeconds: input.positionRef.current,
      paused: input.pausedRef.current,
      sessionStarted: input.startedRef.current,
      sourceGeneration: input.sourcePort,
      applyDecision,
    });
  };

  const decoderHandlers = createDecoderEventHandlers({
    surfaceIdentity,
    streamUrl: media.streamUrl,
    streamStartSeconds: media.streamStartSeconds,
    resumeSeconds: media.resumeSeconds,
    timelineDuration,
    sourceGeneration: input.sourcePort,
    getPosition: () => input.positionRef.current,
    getPaused: () => input.pausedRef.current,
    isSessionStarted: () => input.startedRef.current,
    reconcileSeekProgress: transport.seek.reconcile,
    shouldReportProgress: input.shouldReportProgress,
    shouldPlayNext: () => Boolean(
      input.preferences.autoplayNext &&
      input.nextEpisodePromptEnabled &&
      media.nextEpisode,
    ),
    applyDecision,
    onError: handlePlaybackError,
  });

  return {
    media,
    transport,
    timelineDuration,
    surfaceIdentity,
    decoderHandlers,
    actions: {
      applyDecision,
      seek,
      selectSource,
      handlePlaybackError,
      retryPlayback: () => applyDecision(planPlaybackRetry({
        positionSeconds: input.positionRef.current,
        paused: input.pausedRef.current,
        sessionStarted: input.startedRef.current,
      })),
      togglePaused: () => applyDecision(planTogglePaused(input.pausedRef.current)),
      toggleMuted: () => applyDecision(planToggleMuted(input.mutedRef.current)),
      adjustVolume: (direction: -1 | 1) =>
        applyDecision(planAdjustVolume(input.lifecycleVolume, direction)),
      setVolume: (value: number) => applyDecision(planSetVolume(value)),
      suspend: () => applyDecision(planSuspendPlayback({
        sessionStarted: input.startedRef.current,
        paused: input.pausedRef.current,
      })),
    },
  };
}
